<template>
  <div class="add-timer">
      <form class="omnibox" @submit.prevent="addTimer">
        <div class="omnibox-container" :class="{ 'has-pills': hasProjectsTags && parser.hasPills.value }">
          <div class="omnibox-row">
            <div class="omnibox-input-wrap">
              <input
                type="text"
                id="task-input"
                ref="taskInput"
                v-model="parser.inputValue.value"
                name="task"
                class="omnibox-input"
                :placeholder="omniboxPlaceholder"
                maxlength="40"
                required
                @keydown="handleKeydown"
                @input="parser.onInput()"
                @blur="onBlur"
              >
              <div class="omnibox-actions">
                <button
                  v-if="hasTimerNotes"
                  type="button"
                  class="omnibox-icon-btn"
                  title="Add note"
                  @click="openNoteEditor"
                  ref="noteButton"
                >
                  <span class="icon">
                    <FontAwesomeIcon :icon="faNoteSticky" />
                  </span>
                </button>
                <button type="submit" class="omnibox-add-btn" title="Add timer">
                  <span class="icon">
                    <FontAwesomeIcon :icon="faPlus" />
                  </span>
                </button>
              </div>
              <AutocompleteDropdown
                v-if="hasProjectsTags"
                :visible="parser.showAutocomplete.value"
                :items="parser.autocompleteItems.value"
                :trigger-char="parser.autocompleteType.value === 'project' ? '@' : '#'"
                :query="parser.currentTokenText.value"
                :show-create="parser.showCreateOption.value"
                :selected-index="parser.selectedAutocompleteIndex.value"
                @select="parser.selectItem($event)"
                @create="parser.createFromToken()"
                @hover="parser.selectedAutocompleteIndex.value = $event"
              />
            </div>
          </div>
          <!-- Pill bar (Plus only) -->
          <div v-if="hasProjectsTags && parser.hasPills.value" class="pill-bar">
            <span v-if="parser.committedProject.value" class="tag is-medium pill-tag pill-project" :class="{ 'pill-tag--inherited': parser.inheritedFromContext.value }">
              @{{ parser.committedProject.value.name }}
              <button type="button" class="delete is-small" @click="parser.removeProject()"></button>
            </span>
            <span
              v-for="(tag, index) in parser.committedTags.value"
              :key="tag.name"
              class="tag is-medium pill-tag pill-tag-item" :class="{ 'pill-tag--inherited': parser.inheritedFromContext.value }"
            >
              #{{ tag.name }}
              <button type="button" class="delete is-small" @click="parser.removeTag(index)"></button>
            </span>
          </div>
          <div
            v-if="hasTimerNotes"
            ref="notePanel"
            class="note-panel"
            v-show="showNoteEditor"
          >
            <textarea ref="noteTextarea"></textarea>
          </div>
        </div>
      </form>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useBlocksStore } from '../stores/blocks'
import { useOmniboxParser } from '../composables/useOmniboxParser'
import { useMarkdownEditor } from '../composables/useMarkdownEditor'
import AutocompleteDropdown from './AutocompleteDropdown.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faPlus, faNoteSticky } from '@fortawesome/free-solid-svg-icons'

const store = useBlocksStore()
const parser = useOmniboxParser(store)
const taskInput = ref(null)
const noteButton = ref(null)
const notePanel = ref(null)
const noteTextarea = ref(null)
const noteValue = ref('')
const showNoteEditor = ref(false)
const { editor: noteEditor } = useMarkdownEditor(noteTextarea, noteValue, {
  placeholder: 'Add a note (markdown supported)'
})

const hasProjectsTags = computed(() => store.hasEntitlement('projects_tags'))
const hasTimerNotes = computed(() => store.hasEntitlement('timer_notes'))

const omniboxPlaceholder = computed(() =>
  hasProjectsTags.value
    ? 'What are you working on? Add @projects or #tags'
    : 'What are you working on?'
)

  // Keep inherited project/tag pills in sync with the active context.
  watch(() => store.activeContext, (ctx) => {
    if (!hasProjectsTags.value) return

    if (!ctx) {
      parser.clearInheritedContext()
      return
    }

    if (parser.inheritedFromContext.value) {
      parser.prefillFromContext(ctx)
      return
    }

    if (!parser.inputValue.value && !parser.committedProject.value && !parser.committedTags.value.length) {
      parser.prefillFromContext(ctx)
    }
  }, { immediate: true })

  const handleKeydown = (e) => {
    if (hasProjectsTags.value) {
      parser.onKeydown(e)
      if (e.defaultPrevented) return
    }

    if (
      hasTimerNotes.value &&
      !e.shiftKey &&
      e.key === 'Tab' &&
      !showNoteEditor.value
    ) {
      const inputEl = taskInput.value
      const atEnd = inputEl && inputEl.selectionStart === inputEl.selectionEnd && inputEl.selectionStart === inputEl.value.length
      if (atEnd) {
        e.preventDefault()
        openNoteEditor()
        return
      }
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      addTimer()
    }
  }

  const onBlur = () => {
    setTimeout(() => {
      parser.cancelToken({ removeFromInput: true })
    }, 150)
  }

  const addTimer = () => {
    if (hasProjectsTags.value) {
      parser.finalizeActiveToken()
    }

    const payload = parser.getPayload()
    if (payload.title) {
      store.addTimer({
        description: payload.title,
        duration: 25,
        ...(hasProjectsTags.value && {
          projectId: payload.projectId,
          projectName: payload.projectName,
          tagIds: payload.tagIds,
          tagNames: payload.tagNames,
        }),
        ...(hasTimerNotes.value && { notes: noteValue.value }),
      })
      parser.reset()
      closeNoteEditor(false)
      noteValue.value = ''
      // Re-prefill from active context for the next timer
      if (hasProjectsTags.value && store.activeContext) {
        parser.prefillFromContext(store.activeContext)
      }
    }
  }

  const openNoteEditor = async () => {
    if (!hasTimerNotes.value) return
    showNoteEditor.value = true
    await nextTick()
    if (noteEditor.value) {
      noteEditor.value.codemirror.refresh()
      noteEditor.value.codemirror.focus()
    }
  }

  const closeNoteEditor = (clearValue = false) => {
    if (clearValue) {
      noteValue.value = ''
    }
    showNoteEditor.value = false
  }

  const handleClickOutside = (event) => {
    if (!showNoteEditor.value) return
    const target = event.target
    if (
      notePanel.value?.contains(target) ||
      noteButton.value?.contains(target)
    ) {
      return
    }
    closeNoteEditor()
  }

  const handleEscape = (event) => {
    if (event.key === 'Escape' && showNoteEditor.value) {
      closeNoteEditor()
    }
  }

  onMounted(() => {
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('mousedown', handleClickOutside)
    document.removeEventListener('keydown', handleEscape)
  })

  watch(noteEditor, (instance) => {
    if (!instance) return
    const submit = () => addTimer()
    instance.codemirror.addKeyMap({
      'Cmd-Enter': submit,
      'Ctrl-Enter': submit,
      'Esc': () => closeNoteEditor(),
    })
  })
  </script>

<style scoped>
  .add-timer {
    width: 100%;
    overflow: visible;
  }

  .add-timer::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -28px;
    height: 28px;
    background: linear-gradient(
      to bottom,
      var(--bulma-scheme-main) 0%,
      var(--bulma-scheme-main) 6px,
      transparent 100%
    );
    pointer-events: none;
    z-index: 1;
  }

  .omnibox {
    position: relative;
    overflow-anchor: none;
  }

  .omnibox-container {
    --context-inherited-chip-bg: rgba(213, 99, 45, 0.1);
    --context-inherited-text: hsl(19deg, 67%, 35%);
    border: 1px solid var(--bulma-border);
    border-radius: 8px;
    background: var(--bulma-scheme-main);
    transition: border-color 0.2s;
  }

  .omnibox-container:focus-within {
    border-color: var(--bulma-link);
  }

  .omnibox-row {
    display: flex;
    align-items: center;
  }

  .omnibox-input-wrap {
    flex: 1;
    position: relative;
  }

  .omnibox-input {
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    padding: 0.5em 90px 0.5em 0.75em;
    font-size: 1rem;
    color: var(--bulma-text);
    line-height: 1.5;
  }

  .omnibox-input::placeholder {
    color: var(--bulma-text-weak);
  }

  .omnibox-actions {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .omnibox-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: none;
    background: transparent;
    color: var(--bulma-text-weak);
    cursor: pointer;
    border-radius: 4px;
  }

  .omnibox-icon-btn:hover {
    color: var(--bulma-text);
    background: var(--bulma-background);
  }

  .omnibox-add-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    background: var(--bulma-primary);
    color: #fff;
    cursor: pointer;
    border-radius: 50%;
    font-size: 0.75rem;
  }

  .omnibox-add-btn:hover {
    background: var(--bulma-primary-dark);
  }

  .pill-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px 12px;
    background: var(--bulma-scheme-main-bis);
    border-radius: 0 0 7px 7px;
  }

  .pill-tag {
    border-radius: 12px;
    font-size: 0.8rem;
  }

  .pill-project {
    background: var(--bulma-link-light);
    color: var(--bulma-link-dark);
  }

  .pill-tag-item {
    background: var(--bulma-info-light);
    color: var(--bulma-info-dark);
  }

  .pill-tag--inherited {
    background: var(--context-inherited-chip-bg);
    color: var(--context-inherited-text);
  }

  .note-panel {
    border-top: 2px solid var(--bulma-border);
    padding: 0;
  }

  .note-panel :deep(.EasyMDEContainer) {
    display: flex;
    flex-direction: column;
  }

  .note-panel :deep(.editor-toolbar) {
    border: none;
    border-bottom: 1px solid var(--bulma-border);
    border-radius: 0;
    margin: 0;
  }

  .note-panel :deep(.CodeMirror) {
    border: none;
    border-radius: 0 0 8px 8px;
    min-height: 110px;
    max-height: 160px;
    padding: 0.5rem 0.75rem;
  }

  .note-panel :deep(.CodeMirror-scroll) {
    max-height: 160px;
  }

  @media (prefers-color-scheme: dark) {
    .omnibox-container {
      --context-inherited-chip-bg: rgba(213, 99, 45, 0.14);
      --context-inherited-text: hsl(19deg, 72%, 74%);
    }
  }
</style>
